import api from "@/services/api";
import {
  Recommendation,
  RecommendationType,
  RecommendationTypeDetail,
  RecommendationTypeDetails,
} from "@/services/recommendations/recommendations";
import {
  RecommendationTask,
  GoogleAdsOverlappingKeywordsAdGroupMetrics,
  RecommendationTaskStatus,
} from "@/services/recommendations/tasks";
import { ResourcesDatafilter } from "@/services/data_filters";
import {
  InfiniteData,
  type QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/vue-query";
import { captureException } from "@sentry/vue";
import { computed, reactive, Ref, toRef } from "vue";
import { useDataFilters, useDataFiltersStore } from "@/store/data_filters";
import { QueryFunctionContext } from "@/utils/typing";
import { circumventAdBlocker, PaginatedResponse } from "@/services/utils";

// Bulk recommendation mutations (dismiss / undismiss / assign / per-task
// status writes) all need to refresh the same five caches. Centralized so
// a future cache key addition lands in one place — drift here has bitten
// the PR multiple times. Singular "recommendation" is a separate cache
// from "recommendations" (detail vs list) and TanStack matches prefixes
// literally, so both must be invalidated explicitly.
export function invalidateRecommendationCaches(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: ["recommendations"] });
  queryClient.invalidateQueries({ queryKey: ["recommendation"] });
  queryClient.invalidateQueries({ queryKey: ["recommendationTasks"] });
  queryClient.invalidateQueries({ queryKey: ["recommendationTask"] });
  queryClient.invalidateQueries({ queryKey: ["myTasks"] });
}

function useIncludeWebsiteParam() {
  const store = useDataFiltersStore();
  const flag = toRef(store, "includeWebsiteRecommendations");
  return {
    include_website: computed(() => (flag.value ? true : null)),
  };
}

function listRecommendations({
  queryKey: [, params],
}: QueryFunctionContext<
  {
    externalId: string | undefined;
    page: number | undefined;
    search: string | undefined;
  } & ResourcesDatafilter
>): Promise<Recommendation[]> {
  const recommendationTypes = new Set(Object.values(RecommendationType));
  return api
    .get<Recommendation[]>("/recommendations/", { params })
    .then((response) => {
      response.data = response.data.filter((recommendation: Recommendation) =>
        recommendationTypes.has(recommendation.name),
      );
      return response;
    })
    .then((response) => response.data);
}

export function useRecommendationsQuery({
  externalId,
  page,
  search,
  enabled,
}: {
  externalId?: Ref<string | undefined>;
  page?: Ref<number>;
  search?: Ref<string | undefined>;
  enabled?: Ref<boolean>;
} = {}) {
  const queryKey = [
    "recommendations",
    {
      externalId,
      page,
      search,
      ...useDataFilters(["includedResources"]),
      ...useIncludeWebsiteParam(),
    },
  ] as const;
  return reactive(
    useQuery({
      queryKey,
      queryFn: listRecommendations,
      enabled: enabled ?? true,
    }),
  );
}

function getRecommendation({
  queryKey: [, { recommendationType, ...params }],
}: QueryFunctionContext<
  {
    recommendationType: RecommendationType;
    externalId: string | undefined;
    page: number | undefined;
  } & ResourcesDatafilter
>): Promise<Recommendation> {
  const fixedRecommendationType = circumventAdBlocker(recommendationType);
  return api
    .get<Recommendation>(`/recommendations/${fixedRecommendationType}/`, {
      params,
    })
    .then((response) => response.data);
}

export function useRecommendationQuery({
  recommendationType,
  externalId,
  page,
}: {
  recommendationType: Ref<RecommendationType>;
  externalId?: Ref<string | undefined>;
  page?: Ref<number | undefined>;
}) {
  const dataFilters = useDataFilters(["includedResources"]);
  const includeWebsite = useIncludeWebsiteParam();
  const queryKey = [
    "recommendation",
    {
      recommendationType,
      externalId,
      page,
      ...dataFilters,
      ...includeWebsite,
    },
  ] as const;
  const queryClient = useQueryClient();
  return reactive(
    useQuery({
      queryKey,
      queryFn: getRecommendation,
      initialData: () => {
        return queryClient
          .getQueriesData<Recommendation[] | undefined>({
            queryKey: [
              "recommendations",
              {
                externalId,
                page,
                ...dataFilters,
                ...includeWebsite,
              },
            ],
          })
          .flatMap(([_, recommendations]) => recommendations ?? [])
          .find(
            (recommendation) =>
              recommendation.name === recommendationType.value,
          );
      },
    }),
  );
}

function getRecommendationTask({
  queryKey: [, { recommendationType, pk, ...params }],
}: QueryFunctionContext<{
  pk: number;
  recommendationType: RecommendationType;
}>): Promise<RecommendationTask> {
  const fixedRecommendationType = circumventAdBlocker(recommendationType);
  return api
    .get(`/recommendations/${fixedRecommendationType}/tasks/${pk}/`, { params })
    .then((response) => response.data);
}

export function useRecommendationTaskQuery({
  pk,
  recommendationType,
  enabled,
}: {
  pk: Ref<number>;
  recommendationType: Ref<RecommendationType>;
  externalId?: Ref<string | undefined>;
  page?: Ref<number | undefined>;
  enabled?: Ref<boolean>;
}) {
  const queryKey = [
    "recommendationTask",
    {
      pk,
      recommendationType,
    },
  ] as const;
  return reactive(
    useQuery({
      queryKey,
      queryFn: getRecommendationTask,
      enabled,
    }),
  );
}

function listRecommendationTasks({
  pageParam,
  queryKey: [, { recommendationType, ...params }],
}: QueryFunctionContext<
  {
    recommendationType: RecommendationType;
    externalId: string | undefined;
    page: number | undefined;
    statuses: RecommendationTaskStatus[] | undefined;
    search: string | undefined;
  } & ResourcesDatafilter,
  string | undefined
>): Promise<PaginatedResponse<RecommendationTask>> {
  const fixedRecommendationType = circumventAdBlocker(recommendationType);
  return api
    .get(`/recommendations/${fixedRecommendationType}/tasks/`, {
      params: { cursor: pageParam, ...params },
    })
    .then((response) => response.data);
}

// Rows from another list or search would stay clickable under the loading
// overlay, so only the same list and search keep them. Other key changes
// keep the old rows to avoid a flash.
export function keepPreviousRowsForSameList(current: {
  recommendationType: RecommendationType;
  search: string | undefined;
}) {
  return <TData>(
    previousData: TData | undefined,
    previousQuery?: { queryKey: readonly unknown[] },
  ): TData | undefined => {
    const params = previousQuery?.queryKey[1];
    if (typeof params !== "object" || params === null) return undefined;
    const previous = params as Partial<typeof current>;
    return previous.recommendationType === current.recommendationType &&
      previous.search === current.search
      ? previousData
      : undefined;
  };
}

export function useRecommendationTasksInfiniteQuery({
  recommendationType,
  externalId,
  page,
  statuses,
  search,
  enabled,
}: {
  recommendationType: Ref<RecommendationType>;
  externalId?: Ref<string | undefined>;
  page?: Ref<number | undefined>;
  statuses?: Ref<RecommendationTaskStatus[] | undefined>;
  search?: Ref<string | undefined>;
  enabled?: Ref<boolean>;
}) {
  const queryKey = [
    "recommendationTasks",
    {
      recommendationType,
      externalId,
      page,
      statuses,
      search,
      ...useDataFilters(["includedResources"]),
      ...useIncludeWebsiteParam(),
    },
  ] as const;
  return reactive(
    useInfiniteQuery({
      queryKey,
      queryFn: listRecommendationTasks,
      // A new function per list and search: vue-query reuses a placeholder
      // without calling this again while the option itself is unchanged.
      placeholderData: computed(() =>
        keepPreviousRowsForSameList({
          recommendationType: recommendationType.value,
          search: search?.value,
        }),
      ),
      initialPageParam: undefined,
      enabled: () => enabled?.value ?? true,
      getNextPageParam: (lastPage, _pages) => {
        if (lastPage.next !== null) {
          const params = new URL(lastPage.next).searchParams;
          return params.get("cursor") ?? undefined;
        }
        return undefined;
      },
    }),
  );
}

// Pick<RecommendationTask, "status" | "assignee"> as a Partial alone lets
// {} type-check, which produces an empty PATCH body and a backend 400.
// This union forces callers to supply at least one mutable field.
type UpdateRecommendationTaskMutableFields =
  | {
      status: RecommendationTask["status"];
      assignee?: RecommendationTask["assignee"];
    }
  | {
      status?: RecommendationTask["status"];
      assignee: RecommendationTask["assignee"];
    };

export type UpdateRecommendationTaskPayload = Pick<
  RecommendationTask,
  "recommendation" | "pk"
> &
  UpdateRecommendationTaskMutableFields;

function updateRecommendationTask(
  task: UpdateRecommendationTaskPayload,
): Promise<RecommendationTask> {
  const fixedRecommendationType = circumventAdBlocker(task.recommendation);
  const { recommendation: _r, pk: _pk, ...body } = task;
  return api
    .patch(
      `/recommendations/${fixedRecommendationType}/tasks/${task.pk}/`,
      body,
    )
    .then((response) => response.data);
}

export function useUpdateRecommendationTaskMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: updateRecommendationTask,
      onSuccess: (updatedTask, payload) => {
        // Optimistic patch first: surface the new state in the list
        // cache without waiting for the refetch.
        queryClient.setQueriesData<
          InfiniteData<PaginatedResponse<RecommendationTask>>
        >(
          {
            queryKey: [
              "recommendationTasks",
              { recommendationType: updatedTask.recommendation },
            ],
          },
          (recommendationTasks) => {
            if (recommendationTasks) {
              return {
                ...recommendationTasks,
                pages: recommendationTasks.pages.map((paginatedResponse) => ({
                  ...paginatedResponse,
                  results: paginatedResponse.results.map((task) =>
                    task.pk === updatedTask.pk ? updatedTask : task,
                  ),
                })),
              };
            } else {
              return undefined;
            }
          },
        );
        // Then invalidate so dependent caches re-fetch authoritative
        // server state.
        queryClient.invalidateQueries({
          queryKey: [
            "recommendationTasks",
            { recommendationType: payload.recommendation },
          ],
        });
        queryClient.invalidateQueries({ queryKey: ["recommendations"] });
        queryClient.invalidateQueries({
          queryKey: [
            "recommendation",
            { recommendationType: payload.recommendation },
          ],
        });
        queryClient.invalidateQueries({ queryKey: ["myTasks"] });
        // Open task-detail views (useRecommendationTaskQuery) cache by
        // ["recommendationTask", ...] — invalidate the prefix so detail
        // pages re-fetch after assignee/status changes.
        queryClient.invalidateQueries({ queryKey: ["recommendationTask"] });
      },
    }),
  );
}

export function getRecommendationDetails(
  recommendationType: RecommendationType,
): RecommendationTypeDetail {
  return RecommendationTypeDetails[recommendationType] || null;
}

// RecommendationType specific
function getGoogleAdsOverlappingKeywordsAdGroupMetrics({
  queryKey: [, { taskId }],
}: QueryFunctionContext<{
  taskId: number;
}>): Promise<GoogleAdsOverlappingKeywordsAdGroupMetrics> {
  const fixedRecommendationType = circumventAdBlocker(
    RecommendationType.GOOGLE_ADS_OVERLAPPING_KEYWORDS,
  );
  return api
    .get(
      `/recommendations/${fixedRecommendationType}/tasks/${taskId}/get_ad_group_metrics/`,
    )
    .then((response) => response.data);
}

export function useGoogleAdsOverlappingKeywordsAdGroupMetricsQuery({
  taskId,
}: {
  taskId: Ref<number>;
}) {
  return reactive(
    useQuery({
      queryKey: [
        "googleAdsOverlappingKeywordsAdGroupMetrics",
        { taskId },
      ] as const,
      queryFn: getGoogleAdsOverlappingKeywordsAdGroupMetrics,
      staleTime: 5 * 60 * 1000,
    }),
  );
}

// --- Bulk operations ---

function bulkDismissRecommendation(
  recommendationType: string,
): Promise<{ count: number }> {
  const fixed = circumventAdBlocker(recommendationType);
  return api
    .post(`/recommendations/${fixed}/bulk-dismiss/`)
    .then((r) => r.data);
}

export function useBulkDismissRecommendationMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: bulkDismissRecommendation,
      onSuccess: () => invalidateRecommendationCaches(queryClient),
    }),
  );
}

function bulkUndismissRecommendation(
  recommendationType: string,
): Promise<{ count: number }> {
  const fixed = circumventAdBlocker(recommendationType);
  return api
    .post(`/recommendations/${fixed}/bulk-undismiss/`)
    .then((r) => r.data);
}

export function useBulkUndismissRecommendationMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: bulkUndismissRecommendation,
      onSuccess: () => invalidateRecommendationCaches(queryClient),
    }),
  );
}

function bulkAssignRecommendation(payload: {
  recommendationType: string;
  assigneeId: number | null;
}): Promise<{ count: number }> {
  const fixed = circumventAdBlocker(payload.recommendationType);
  return api
    .post(`/recommendations/${fixed}/bulk-assign/`, {
      assigneeId: payload.assigneeId,
    })
    .then((r) => r.data);
}

export function useBulkAssignRecommendationMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: bulkAssignRecommendation,
      // Reconcile on both outcomes via onSettled so success and failure can't
      // drift on which caches they invalidate. On error also capture to Sentry
      // so a failed claim/assign surfaces rather than failing silently.
      onError: (err) => captureException(err),
      onSettled: () => invalidateRecommendationCaches(queryClient),
    }),
  );
}

// --- My Tasks ---

export interface MyTaskItem {
  type: "recommendation" | "ai_task";
  recommendation: RecommendationType;
  taskPk?: number;
  title?: string;
  category?: string;
  status?: RecommendationTaskStatus;
  impactSummary?: string;
  myCount?: number;
  totalCount?: number;
  doneCount?: number;
  inProgressCount?: number;
  dismissedCount?: number;
  totalSteps?: number;
  doneSteps?: number;
  createdAt: string;
  modifiedAt: string;
}

function updateMyTaskStatus(payload: {
  recommendation: RecommendationType;
  status: RecommendationTaskStatus;
  taskPk?: number;
}): Promise<{ count: number }> {
  return api
    .post("/recommendations/my-tasks/update-status/", {
      recommendation: payload.recommendation,
      status: payload.status,
      taskPk: payload.taskPk,
    })
    .then((r) => r.data);
}

export function useUpdateMyTaskStatusMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: updateMyTaskStatus,
      // Reconcile on both outcomes via onSettled so success and an errored
      // status PATCH (incl. swallowed AI 403s) invalidate the exact same set —
      // a failure mustn't leave the row showing the optimistic new status, and
      // the two paths can't drift as keys are added. On error also capture to
      // Sentry.
      onError: (err) => captureException(err),
      onSettled: () => {
        invalidateRecommendationCaches(queryClient);
        // AI tracking detail page renders status badges sourced from
        // the parent task; without this the open page won't reflect a
        // mark-DONE / dismiss until manual refresh.
        queryClient.invalidateQueries({
          queryKey: ["ai-recommendation-tracking"],
        });
        // The AI suggestions feed/cards source from a separate cache
        // key — without this, dismissing or restoring an AI task from
        // My Tasks leaves the feed stuck on the previous state until
        // some unrelated refetch clears it.
        queryClient.invalidateQueries({
          queryKey: ["ai-suggestions"],
        });
      },
    }),
  );
}

function listMyTasks(): Promise<MyTaskItem[]> {
  return api
    .get<MyTaskItem[]>("/recommendations/my-tasks/")
    .then((r) => r.data);
}

export function useMyTasksQuery({
  enabled,
}: {
  enabled?: Ref<boolean>;
} = {}) {
  return reactive(
    useQuery({
      queryKey: ["myTasks"] as const,
      queryFn: listMyTasks,
      // Gate the fetch: the slideover panel mounts in the global shell
      // and would otherwise hit `/my-tasks/` on every page load even
      // when the user never opens it.
      enabled: enabled ?? true,
    }),
  );
}
